import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/atoms';
import { useCreateOverride, useWorkflow } from '@/hooks/useWorkflows';

import styles from './WorkflowOverride.module.css';

export function WorkflowOverride() {
  const params = useParams<{ id: string }>();
  const id = params.id!;
  const navigate = useNavigate();
  const { data: workflow } = useWorkflow(id);
  const createMutation = useCreateOverride();

  const [name, setName] = useState('Override');
  const [reason, setReason] = useState('');
  const [scheduleHours, setScheduleHours] = useState<string>('');
  const [scheduleCron, setScheduleCron] = useState<string>('');
  const [activateImmediately, setActivateImmediately] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function handleCreate() {
    if (!name.trim()) {
      setMsg('Nome obrigatório.');
      return;
    }
    setMsg(null);
    try {
      const payload = {
        name: name.trim(),
        reason: reason.trim() || undefined,
        changes: {
          schedule_hours_interval: scheduleHours ? Number(scheduleHours) : undefined,
          schedule_cron: scheduleCron || undefined,
        },
        activate_immediately: activateImmediately,
      };
      const result = await createMutation.mutateAsync({ id, payload });
      setMsg(`Override criado: ${result.override_workflow_id}`);
      setTimeout(() => navigate(`/workflows/${result.override_workflow_id}`), 1000);
    } catch (e) {
      setMsg(`Erro: ${String(e)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <Link to={`/workflows/${id}`} className={styles.back}>
          ← Workflow
        </Link>
        <h1 className={styles.title}>Criar override</h1>
      </header>

      {workflow && (
        <p className={styles.basedOn}>
          Base: <strong>{workflow.name}</strong> ({workflow.id})
        </p>
      )}

      <div className={styles.warning}>
        O workflow original não será alterado (Axioma II). Será criado um novo
        workflow no N8N com as alterações abaixo.
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Nome do override *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={styles.input}
          placeholder="ex: 4h schedule teste"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Motivo (opcional)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className={styles.textarea}
          placeholder="Por que este override foi criado?"
        />
      </div>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Alterações ao schedule</legend>
        <div className={styles.field}>
          <label className={styles.label}>Intervalo (horas)</label>
          <input
            type="number"
            min="1"
            value={scheduleHours}
            onChange={(e) => {
              setScheduleHours(e.target.value);
              if (e.target.value) setScheduleCron('');
            }}
            className={styles.input}
            placeholder="(deixar vazio para não alterar)"
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>Ou cron expression</label>
          <input
            type="text"
            value={scheduleCron}
            onChange={(e) => {
              setScheduleCron(e.target.value);
              if (e.target.value) setScheduleHours('');
            }}
            className={styles.input}
            placeholder="0 */4 * * *"
          />
        </div>
      </fieldset>

      <label className={styles.checkbox}>
        <input
          type="checkbox"
          checked={activateImmediately}
          onChange={(e) => setActivateImmediately(e.target.checked)}
        />
        Activar override imediatamente (original continua activo)
      </label>

      <div className={styles.actions}>
        {msg && <span className={styles.msg}>{msg}</span>}
        <Button variant="ghost" onClick={() => navigate(`/workflows/${id}`)}>
          Cancelar
        </Button>
        <Button onClick={handleCreate} loading={createMutation.isPending}>
          Criar duplicado
        </Button>
      </div>
    </section>
  );
}
